"use client";

import { cn } from "@ui/lib";
import { PauseIcon, PlayIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { useIsClient } from "usehooks-ts";

/**
 * A short feature explainer rendered by `tooling/video` into
 * /public/videos/<id>-<light|dark>.{mp4,jpg}.
 *
 * Muted and looping; autoplays only while on screen. Whenever it isn't
 * playing (autoplay blocked by the browser, reduced motion, or paused by the
 * viewer) the poster carries a play button, so it always reads as a video.
 * While playing, a pause button stays available (WCAG 2.2.2).
 */
export function FeatureVideo({
	id,
	label,
	className,
}: {
	/** Video id from tooling/video/src/videos/manifest.json. */
	id: string;
	/** Accessible description of what the video shows. */
	label: string;
	className?: string;
}) {
	const t = useTranslations("marketing.featureVideo");
	const { resolvedTheme } = useTheme();
	const isClient = useIsClient();
	const mode = isClient && resolvedTheme === "dark" ? "dark" : "light";
	const ref = useRef<HTMLVideoElement>(null);
	const [reducedMotion, setReducedMotion] = useState(false);
	const [playing, setPlaying] = useState(false);
	// Once the viewer pauses, scrolling back into view must not restart it.
	const pausedByViewer = useRef(false);

	useEffect(() => {
		const query = window.matchMedia("(prefers-reduced-motion: reduce)");
		setReducedMotion(query.matches);
		const onChange = (e: MediaQueryListEvent) =>
			setReducedMotion(e.matches);
		query.addEventListener("change", onChange);
		return () => query.removeEventListener("change", onChange);
	}, []);

	// `mode` is a dependency so the observer re-attaches when a theme switch
	// swaps in the other video element.
	useEffect(() => {
		const video = ref.current;
		if (!video || reducedMotion) {
			return;
		}
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					if (!pausedByViewer.current) {
						video.play().catch(() => {
							// Autoplay refused: the play button stays up.
						});
					}
				} else {
					video.pause();
				}
			},
			{ threshold: 0.35 },
		);
		observer.observe(video);
		return () => observer.disconnect();
	}, [reducedMotion, mode]);

	const play = () => {
		pausedByViewer.current = false;
		ref.current?.play().catch(() => {
			// Nothing left to try; the button stays so the viewer can retry.
		});
	};

	const pause = () => {
		pausedByViewer.current = true;
		ref.current?.pause();
	};

	return (
		<div
			className={cn(
				"relative overflow-hidden rounded-2xl border bg-card shadow-sm",
				className,
			)}
		>
			<video
				key={mode}
				ref={ref}
				className="block aspect-video w-full"
				src={`/videos/${id}-${mode}.mp4`}
				poster={`/videos/${id}-${mode}.jpg`}
				aria-label={label}
				muted
				loop
				playsInline
				preload={reducedMotion ? "none" : "metadata"}
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
				onEmptied={() => setPlaying(false)}
			/>

			{playing ? (
				<button
					type="button"
					onClick={pause}
					aria-label={t("pause")}
					className="absolute right-3 bottom-3 flex size-10 items-center justify-center rounded-full border bg-background/80 text-foreground shadow-sm backdrop-blur transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<PauseIcon className="size-4" aria-hidden />
				</button>
			) : (
				<button
					type="button"
					onClick={play}
					aria-label={t("play")}
					className="group absolute inset-0 flex items-center justify-center bg-foreground/5 transition-colors hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
				>
					<span className="flex items-center gap-3 rounded-full bg-primary py-3 pr-6 pl-4 font-semibold text-primary-foreground shadow-lg transition-transform group-hover:scale-105">
						<span className="flex size-9 items-center justify-center rounded-full bg-primary-foreground/20">
							<PlayIcon
								className="size-4 fill-current"
								aria-hidden
							/>
						</span>
						{t("play")}
					</span>
				</button>
			)}
		</div>
	);
}
